import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse, Loader2, Check, Eye, Droplet } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* MediCard — the citizen-maintained medical card. Medics pull it up in Triage the
   moment you're on their table: blood type, allergies, conditions, donor status. */

const BLOOD = ["O-", "O+", "A-", "A+", "B-", "B+", "AB-", "AB+", "unknown"];
const DEMO_CARD = { blood_type: "O-", allergies: "morphine", conditions: "asthma", organ_donor: true, updated_at: "" };

export default function MediCardApp({ demo, serverId, token, accent, sfx }) {
  const [card, setCard] = useState(demo ? DEMO_CARD : null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/medical/card`, { params: { token } })
      .then((r) => setCard(r.data.card)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (busy) return;
    setBusy(true); sfx?.keyTick();
    try {
      if (demo) { toast.success("Preview: medical card saved (demo)"); return; }
      const r = await api.post(`/nc/servers/${serverId}/medical/card`, card, { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!card) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/50" /></div>;

  return (
    <div data-testid="medicard-app" className="flex-1 flex flex-col gap-3 overflow-y-auto pb-4" style={{ scrollbarWidth: "none" }}>
      <p className="font-sans font-bold text-white text-lg">MediCard</p>

      {/* the card as medics see it */}
      <div className="border border-red-500/30 bg-gradient-to-br from-red-500/[0.08] to-transparent rounded-3xl p-5">
        <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-red-400 flex items-center gap-2"><HeartPulse className="w-3.5 h-3.5" /> Emergency medical card</p>
        <div className="flex items-center gap-4 mt-4">
          <div className="w-16 h-16 rounded-2xl border-2 border-red-500/50 bg-red-500/10 flex flex-col items-center justify-center">
            <Droplet className="w-4 h-4 text-red-400" />
            <span className="font-sans font-bold text-lg text-red-300 mt-0.5">{card.blood_type}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[9px] text-white/70 leading-relaxed"><span className="text-white/40">ALLERGIES:</span> {card.allergies || "none listed"}</p>
            <p className="font-mono text-[9px] text-white/70 leading-relaxed mt-1"><span className="text-white/40">CONDITIONS:</span> {card.conditions || "none listed"}</p>
            <p className="font-mono text-[8px] text-white/50 mt-1.5 uppercase tracking-[0.15em]">{card.organ_donor ? "organ donor ✓" : "not a donor"}</p>
          </div>
        </div>
        <p className="font-mono text-[7px] text-white/30 mt-4 uppercase tracking-[0.2em] flex items-center gap-1"><Eye className="w-3 h-3" /> visible to medics & triage staff only</p>
      </div>

      {/* editor */}
      <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-4 flex flex-col gap-3">
        <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">Update your card</p>
        <div className="grid grid-cols-4 gap-1.5">
          {BLOOD.map((b) => (
            <button key={b} data-testid={`medi-blood-${b.replace(/[^A-Z0-9]/gi, "")}`} onClick={() => { setCard({ ...card, blood_type: b }); sfx?.keyTick(); }}
              className={`py-2 rounded-xl font-mono text-[9px] font-bold border transition-colors ${card.blood_type === b ? "border-red-500/60 text-red-300 bg-red-500/10" : "border-white/10 text-white/40"}`}>{b}</button>
          ))}
        </div>
        <input data-testid="medi-allergies" value={card.allergies} maxLength={200} onChange={(e) => setCard({ ...card, allergies: e.target.value })}
          placeholder="Allergies (e.g. morphine, latex)"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white placeholder:text-white/30 focus:outline-none" />
        <input data-testid="medi-conditions" value={card.conditions} maxLength={200} onChange={(e) => setCard({ ...card, conditions: e.target.value })}
          placeholder="Conditions (e.g. asthma, diabetes)"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white placeholder:text-white/30 focus:outline-none" />
        <label data-testid="medi-donor" className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={card.organ_donor} onChange={(e) => setCard({ ...card, organ_donor: e.target.checked })} className="w-4 h-4 accent-red-500" />
          <span className="font-mono text-[9px] text-white/60">Register as an organ donor</span>
        </label>
        <button data-testid="medi-save" onClick={save} disabled={busy}
          className="w-full py-3 rounded-xl font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Save card</>}
        </button>
        <p className="font-mono text-[7px] text-white/25 text-center uppercase tracking-[0.2em]">paired with 911 — medics see this before they touch you</p>
      </div>
    </div>
  );
}
